import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * Site-gate invites — per-person links past vera.inc's pre-launch password
 * gate (`vera.inc/enter?invite=<token>`). API: apps/api/src/features/site-gate-invites.
 *
 * `status` is computed by the API (revoked → expired → unused → active), so the
 * list and the chip can never disagree about what a row means.
 */
export type SiteGateInviteStatus = 'active' | 'unused' | 'expired' | 'revoked'

export type SiteGateInvite = {
  id: string
  label: string
  email: string | null
  note: string | null
  status: SiteGateInviteStatus
  created_at: string
  created_by_user_id: string | null
  expires_at: string
  revoked_at: string | null
  first_used_at: string | null
  last_used_at: string | null
  use_count: number
  last_used_country: string | null
  last_used_ua: string | null
}

/** Returned by create ONLY. `url` is the full link and is never retrievable again. */
export type CreatedSiteGateInvite = SiteGateInvite & { url: string }

export type CreateSiteGateInvitePayload = {
  label: string
  email?: string
  note?: string
  expires_at: string
}

export const SITE_GATE_INVITES_QUERY_KEY = 'site-gate-invites'

export const useSiteGateInvites = () =>
  useQuery<SiteGateInvite[]>({
    queryKey: [SITE_GATE_INVITES_QUERY_KEY],
    queryFn: async () => {
      const response = await createApiCall({ method: 'GET', url: '/internal/site-gate-invites' })
      if (response.status >= 400) throw new Error(response.data?.message || 'Failed to load invites')
      return response.data as SiteGateInvite[]
    },
    staleTime: 1000 * 30,
  })

export const useCreateSiteGateInvite = (
  options?: Omit<UseMutationOptions<CreatedSiteGateInvite, Error, CreateSiteGateInvitePayload>, 'mutationFn'>
) => {
  const queryClient = useQueryClient()
  return useMutation<CreatedSiteGateInvite, Error, CreateSiteGateInvitePayload>({
    mutationFn: async (data) => {
      const response = await createApiCall({ method: 'POST', url: '/internal/site-gate-invites', data })
      if (response.status >= 400) throw new Error(response.data?.message || 'Failed to create invite')
      return response.data as CreatedSiteGateInvite
    },
    ...options,
    // Rest-args rather than `(data, variables, context)`: react-query v5's onSuccess
    // takes FOUR parameters, and forwarding three is a TS2554 (the sibling hooks carry it).
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [SITE_GATE_INVITES_QUERY_KEY] })
      options?.onSuccess?.(...args)
    },
  })
}

export const useRevokeSiteGateInvite = (
  options?: Omit<UseMutationOptions<SiteGateInvite, Error, { id: string }>, 'mutationFn'>
) => {
  const queryClient = useQueryClient()
  return useMutation<SiteGateInvite, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const response = await createApiCall({ method: 'POST', url: `/internal/site-gate-invites/${id}/revoke` })
      if (response.status >= 400) throw new Error(response.data?.message || 'Failed to revoke invite')
      return response.data as SiteGateInvite
    },
    ...options,
    // Rest-args rather than `(data, variables, context)`: react-query v5's onSuccess
    // takes FOUR parameters, and forwarding three is a TS2554 (the sibling hooks carry it).
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [SITE_GATE_INVITES_QUERY_KEY] })
      options?.onSuccess?.(...args)
    },
  })
}
